import { BootstrapToolTip } from '@/_elements/ToolTip';
import {
    createPromptTemplate,
    getPromptTemplateById,
    getPromptTemplates,
    updatePromptTemplate,
} from '@/_services/api/promptTemplate';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import { BsEye, BsPencil } from 'react-icons/bs';
import { FaPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';
import TeamSelectFilter from '../Admin/TeamSelectFilter';
import { VscLoading } from 'react-icons/vsc';
import { getTeamIdFromURL } from '@/_helpers';

export default function PromptTemplateForm({ viewMode, template = null, onEdit = () => {}, onCreate = () => {} }) {
    const [formData, setFormData] = React.useState({
        name: '',
        shortcutKeyword: '',
        description: '',
        promptText: '',
    });

    const teamId = getTeamIdFromURL();
    const [show, setShow] = useState(false);
    const handleShow = () => setShow(true);
    const handleClose = () => setShow(false);
    const [formErrors, setFormErrors] = React.useState({});
    const [submitting, setSubmitting] = React.useState(false);
    const [selectedTeamOptions, setSelectedTeamOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const fetchPromptTemplate = async (template) => {
        try {
            setLoading(true);
            const response = await getPromptTemplateById({ promptTemplateId: template._id });
            setFormData({
                name: response.name || '',
                shortcutKeyword: response.shortcutKeyword || '',
                description: response.description || '',
                promptText: response.promptText || '',
            });

            const _selectedTeams = (response?.teamsData || [])?.map((team) => ({
                label: team.name,
                value: team.id,
            }));
            setSelectedTeamOptions(_selectedTeams);
            setLoading(false);
        } catch (error) {
            if (error.name === 'AbortError') return;
            toast.error(error.message);
        }
    };

    useEffect(() => {
        if (show) {
            if (template) {
                fetchPromptTemplate(template);
                setFormData({
                    name: template.name || '',
                    shortcutKeyword: template.shortcutKeyword || '',
                    description: template.description || '',
                    promptText: template.promptText || '',
                });
                setSelectedTeamOptions(template?.teamData || []);
                setFormErrors({});
            } else {
                setFormData({
                    name: '',
                    shortcutKeyword: '',
                    description: '',
                    promptText: '',
                });
                setFormErrors({});
            }
        }
    }, [template, show]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setFormErrors((prev) => ({ ...prev, [name]: '' }));
    };
    const validatePromptTemplateForm = (data) => {
        const errors = {};
        if (!data.name) {
            errors.name = 'Name is required';
        }
        if (!data.shortcutKeyword) {
            errors.shortcutKeyword = 'Shortcut keyword is required';
        }
        if (!data.promptText) {
            errors.promptText = 'Prompt text is required';
        }
        return errors;
    };

    const handleSubmit = async () => {
        try {
            setFormErrors({});
            const preparedFormData = {
                name: formData.name.trim(),
                shortcutKeyword: formData.shortcutKeyword.trim().toLowerCase(),
                description: formData.description.trim(),
                promptText: formData.promptText.trim(),
            };
            const errors = validatePromptTemplateForm(preparedFormData);
            if (Object.keys(errors).length) {
                setFormErrors(errors);
                return;
            }

            setSubmitting(true);
            let response;
            if (template) {
                if (!template?.team) {
                    preparedFormData.teams = selectedTeamOptions.map((team) => team.value);
                }
                response = await updatePromptTemplate({ data: preparedFormData, promptTemplateId: template._id });
                toast.success('Prompt template updated successfully!');
                onEdit(response);
            } else {
                if (!teamId) {
                    preparedFormData.teams = selectedTeamOptions.map((team) => team.value);
                } else {
                    preparedFormData.team = teamId;
                }
                response = await createPromptTemplate({ data: preparedFormData });
                toast.success('Prompt template created successfully!');
                onCreate(response);
            }
            handleClose();
        } catch (error) {
            toast.error(error?.message || 'Something went wrong!');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Fragment>
            {viewMode ? (
                <BootstrapToolTip text="View">
                    <BsEye className="view icon" onClick={() => handleShow()} />
                </BootstrapToolTip>
            ) : (
                <Fragment>
                    {template ? (
                        <BootstrapToolTip text="Edit">
                            <BsPencil className="edit icon" onClick={() => handleShow()} />
                        </BootstrapToolTip>
                    ) : (
                        <Button variant="primary" onClick={() => handleShow()}>
                            <FaPlus className="mr-1" /> New Prompt Template{' '}
                            {!teamId && <span className="ml-1">Library</span>}
                        </Button>
                    )}
                </Fragment>
            )}

            <Modal show={show} size="lg" backdrop="static">
                <Modal.Header>
                    <Modal.Title>
                        {viewMode ? (
                            'View Prompt Template'
                        ) : (
                            <Fragment>
                                <strong>{template ? 'Edit' : 'Create'}</strong>
                            </Fragment>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loading ? (
                        <h4 className="text-center">
                            <VscLoading className="spin" fontSize="42" />
                        </h4>
                    ) : (
                        <Fragment>
                            <Form.Group className="mb-4">
                                <Form.Label>Template Name</Form.Label>
                                <Form.Control
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="E.g., Form Submission Summary"
                                    isInvalid={!!formErrors.name}
                                    disabled={viewMode}
                                />
                                <Form.Control.Feedback type="invalid">{formErrors.name}</Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-4">
                                <Form.Label>Shortcut Keyword</Form.Label>
                                <Form.Control
                                    name="shortcutKeyword"
                                    value={formData.shortcutKeyword}
                                    onChange={handleChange}
                                    placeholder="E.g., summarize"
                                    isInvalid={!!formErrors.shortcutKeyword}
                                    disabled={viewMode}
                                />
                                <Form.Text className={formErrors.shortcutKeyword ? 'text-danger' : 'text-muted'}>
                                    {formErrors.shortcutKeyword ||
                                        'Team members will type this keyword after /bob or /quinn. Eg: /bob summarize'}
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-4">
                                <Form.Label>Description (Optional)</Form.Label>
                                <Form.Control
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    placeholder="Brief description"
                                    disabled={viewMode}
                                />
                            </Form.Group>

                            <Form.Group className="mb-4">
                                <Form.Label>Prompt Text</Form.Label>
                                <Form.Control
                                    name="promptText"
                                    value={formData.promptText}
                                    onChange={handleChange}
                                    as="textarea"
                                    rows={6}
                                    placeholder="Enter the prompt text"
                                    isInvalid={!!formErrors.promptText}
                                    disabled={viewMode}
                                />
                                <Form.Text className={formErrors.promptText ? 'text-danger' : 'text-muted'}>
                                    {formErrors.promptText || 'Write the prompt that will be sent to the AI'}
                                </Form.Text>
                            </Form.Group>

                            {teamId || (template || {})?.team ? null : (
                                <Form.Group className="mb-4">
                                    <div className="w-100">
                                        <Form.Label>Teams</Form.Label>
                                        <TeamSelectFilter
                                            isMulti
                                            disabled={!!teamId || viewMode}
                                            selectedTeamOption={selectedTeamOptions}
                                            setSelectedTeamOption={setSelectedTeamOptions}
                                        />
                                    </div>
                                </Form.Group>
                            )}
                        </Fragment>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={submitting}>
                        Close
                    </Button>
                    {viewMode ? null : (
                        <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
                            {submitting ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="mr-1"
                                    />
                                    Saving...
                                </>
                            ) : template ? (
                                'Update'
                            ) : (
                                'Create'
                            )}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </Fragment>
    );
}
